import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { Undo2 } from "lucide-react";
import { apiMerchantDefinedFieldsIdFormatGetOptions } from "@/generated/api/@tanstack/react-query.gen";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ButtonIcon } from "@/components/button-icon";
import ContainerInset from "@/components/container-inset";
import { PageErrorNotFound } from "@/components/page-error";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider";
import MerchantDefinedFieldsPreview from "./_components/merchant-defined-fields-preview";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/merchant-defined-fields/$id/preview/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { id } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();
  const merchantAccountId = selectedMerchant?.merchAccountId || "";
  const merchantDefinedFieldsDetailQuery = useQuery({
    ...apiMerchantDefinedFieldsIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": merchantAccountId,
      },
      path: {
        id,
      },
    }),
    enabled: !!merchantAccountId,
  });

  const renderContent = () => {
    if (merchantDefinedFieldsDetailQuery.isLoading || !merchantAccountId) {
      return (
        <div className="space-y-2">
          <Skeleton className="mt-2 h-32 w-full" />
        </div>
      );
    }

    if (
      merchantDefinedFieldsDetailQuery.error ||
      !merchantDefinedFieldsDetailQuery.data
    ) {
      return (
        <PageErrorNotFound
          message={
            merchantDefinedFieldsDetailQuery.error?.message ||
            "Merchant Defined Fields not found"
          }
          bottomRender={
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          }
        />
      );
    }
    return (
      <div className="flex min-h-[40vh] flex-col justify-between">
        <MerchantDefinedFieldsPreview
          detail={merchantDefinedFieldsDetailQuery.data}
        />
        <div className="mt-6 flex justify-end space-x-4">
          <ButtonIcon
            type="button"
            icon={<Undo2 />}
            className="bg-primary"
            onClick={() => {
              router.history.back();
            }}
          >
            Back
          </ButtonIcon>
          <ButtonIcon
            type="button"
            icon={"update"}
            className="bg-green-900 hover:bg-green-800"
            onClick={() => {
              router.navigate({
                to: "/$merchantId/merchant-defined-fields/$id/edit",
                params: {
                  id,
                  merchantId: merchantAccountId,
                },
              });
            }}
          >
            Edit
          </ButtonIcon>
        </div>
      </div>
    );
  };

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Merchant Defined Field",
          href: `/${merchantAccountId}/merchant-defined-fields`,
        },
        {
          title: "Preview",
          href: "",
        },
      ]}
    >
      <PageLayoutRoot>
        <PageLayoutHeader
          title="Preview Merchant Defined Fields"
          description={
            <span>
              Below is a preview of what this field will look like in the
              Virtual Terminal. You may{" "}
              <Link
                to="/$merchantId/merchant-defined-fields/$id/edit"
                className="text-primary hover:underline"
                params={{
                  merchantId: selectedMerchant?.merchAccountId ?? "",
                  id,
                }}
              >
                configure this field here.
              </Link>
            </span>
          }
        />

        <PageLayoutContent>{renderContent()}</PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
